import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { BuilderState, BuilderAction, UnifiedChatbotData, UnifiedChatbotSettings, DEFAULT_UNIFIED_SETTINGS } from '@/types/builder';
import { ChatTheme, Question, TriggerRule } from '@/types/theme';
import { FieldDictionary } from '@/types/fieldsDictionary';
import { useToast } from '@/components/ui/use-toast';
import { useChatbot } from '@/hooks/useChatbot';
import { fetchFieldsDictionary } from '@/services/chatbotDataService';

// Initial state
const initialState: BuilderState = {
  chatbot: null,
  fieldsDictionary: [],
  isLoading: false,
  isSaving: false,
  error: null,
  isDirty: false,
  dirtyFields: new Set(),
  activeTab: 'dashboard',
  unsavedChanges: false,
  pendingOperatorAvatarFile: null,
  removeOperatorAvatarOnSave: false,
  pendingCompanyLogoFile: null,
  removeCompanyLogoOnSave: false
};

// Reducer function
const builderReducer = (state: BuilderState, action: BuilderAction): BuilderState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_CHATBOT':
      return { 
        ...state, 
        chatbot: action.payload,
        isLoading: false,
        error: null
      };
    
    case 'UPDATE_SETTINGS':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          settings: { ...state.chatbot.settings, ...action.payload }
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_THEME':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          theme: { ...state.chatbot.theme, ...action.payload }
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_QUESTIONS':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          questions: action.payload
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_TRIGGER_RULES':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          triggerRules: action.payload
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_BASIC_INFO':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          ...action.payload
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'SET_DIRTY':
      const newDirtyFields = new Set(state.dirtyFields);
      if (action.payload.isDirty) {
        newDirtyFields.add(action.payload.field);
      } else {
        newDirtyFields.delete(action.payload.field);
      }
      return {
        ...state,
        dirtyFields: newDirtyFields,
        isDirty: newDirtyFields.size > 0,
        unsavedChanges: newDirtyFields.size > 0
      };
    
    case 'RESET_DIRTY':
      return {
        ...state,
        isDirty: false,
        dirtyFields: new Set(),
        unsavedChanges: false
      };
    
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    
    case 'SET_FIELDS_DICTIONARY':
      return { ...state, fieldsDictionary: action.payload };
    
    case 'SET_PENDING_OPERATOR_AVATAR':
      return { ...state, pendingOperatorAvatarFile: action.payload };
    
    case 'SET_REMOVE_OPERATOR_AVATAR':
      return { ...state, removeOperatorAvatarOnSave: action.payload };
    
    case 'SET_PENDING_COMPANY_LOGO':
      return { ...state, pendingCompanyLogoFile: action.payload };
    
    case 'SET_REMOVE_COMPANY_LOGO':
      return { ...state, removeCompanyLogoOnSave: action.payload };
    
    case 'CLEAR_PENDING_FILES':
      return {
        ...state,
        pendingOperatorAvatarFile: null,
        removeOperatorAvatarOnSave: false,
        pendingCompanyLogoFile: null,
        removeCompanyLogoOnSave: false
      };
    
    default:
      return state;
  }
};

// Context interface
interface BuilderContextType {
  state: BuilderState;
  dispatch: React.Dispatch<BuilderAction>;
  
  // Convenience methods
  updateSettings: (updates: Partial<UnifiedChatbotSettings>) => void;
  updateTheme: (updates: Partial<ChatTheme>) => void;
  updateQuestions: (questions: Question[]) => void;
  updateTriggerRules: (rules: TriggerRule[]) => void;
  updateBasicInfo: (info: { name?: string; description?: string; title?: string; welcome_message?: string; completion_message?: string; operator_name?: string | null; operator_avatar_url?: string | null; company_name?: string | null; company_logo?: string | null }) => void;
  setDirty: (field: string, isDirty: boolean) => void;
  resetDirty: () => void;
  setActiveTab: (tab: string) => void;
  setPendingOperatorAvatar: (file: File | null) => void;
  setRemoveOperatorAvatar: (remove: boolean) => void;
  setPendingCompanyLogo: (file: File | null) => void;
  setRemoveCompanyLogo: (remove: boolean) => void;
  clearPendingFiles: () => void;
  
  // Data access helpers
  getChatbot: () => UnifiedChatbotData | null;
  getSettings: () => UnifiedChatbotSettings;
  getTheme: () => ChatTheme | null;
  getQuestions: () => Question[];
  getTriggerRules: () => TriggerRule[];
  getFieldsDictionary: () => FieldDictionary[];

  // Save methods
  debouncedSave: (updates: Partial<UnifiedChatbotData>) => void;
  saveTheme: (theme: ChatTheme) => void;
  saveTriggerRules: (triggerRules: TriggerRule[]) => void;
  refetch: () => void;
  
  // Mutations (from useChatbot hook)
  mutations?: {
    addQuestion: (question: Omit<Question, 'id' | 'chatbot_id'>) => Promise<any>;
    updateQuestion: (params: { questionId: string; updates: Partial<Question> }) => Promise<any>;
    deleteQuestion: (questionId: string) => Promise<any>;
    updateQuestionsOrder: (questions: Question[]) => Promise<any>;
    addTriggerRule: () => Promise<any>;
    updateTriggerRule: (params: { ruleId: string; updates: Partial<TriggerRule> }) => Promise<any>;
    deleteTriggerRule: (ruleId: string) => Promise<any>;
  };
}

// Create context
const BuilderContext = createContext<BuilderContextType | undefined>(undefined);

// Provider component
interface BuilderProviderProps {
  children: React.ReactNode;
  chatbotId: string;
}

export const BuilderProvider: React.FC<BuilderProviderProps> = ({ children, chatbotId }) => {
  const [state, dispatch] = useReducer(builderReducer, initialState);
  const { toast } = useToast();



  // Use the unified chatbot hook as the single source of truth
  const chatbotHook = useChatbot(chatbotId);
  const {
    data: unifiedData,
    isLoading,
    error,
    isSaving,
    save: saveChatbotData,
    saveTheme: saveThemeMutation,
    addQuestion: addQuestionMutation,
    updateQuestion: updateQuestionMutation,
    deleteQuestion: deleteQuestionMutation,
    updateQuestionsOrder: updateQuestionsOrderMutation,
    addTriggerRule: addTriggerRuleMutation,
    updateTriggerRule: updateTriggerRuleMutation,
    deleteTriggerRule: deleteTriggerRuleMutation,
    refetch
  } = chatbotHook;

  // Wrapper for debouncedSave (for backward compatibility)
  const debouncedSave = useCallback((updates: Partial<UnifiedChatbotData>) => {
    saveChatbotData(updates);
  }, [saveChatbotData]);

  // Wrapper for saveTheme (for backward compatibility)
  const saveTheme = useCallback(async (theme: ChatTheme) => {
    await saveThemeMutation(theme);
  }, [saveThemeMutation]);

  // Wrapper for saveTriggerRules (for backward compatibility)
  const saveTriggerRules = useCallback(async (triggerRules: TriggerRule[]) => {
    // Import and use saveAllTriggerRules service function
    const { saveAllTriggerRules } = await import('@/services/chatbotMutationsService');
    await saveAllTriggerRules(chatbotId, triggerRules);
  }, [chatbotId]);

  // Update context state when unified data changes
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, [error]);

  useEffect(() => {
    dispatch({ type: 'SET_SAVING', payload: isSaving });
  }, [isSaving]);

  // Load fields dictionary on mount
  useEffect(() => {
    const loadFieldsDictionary = async () => {
      try {
        const fields = await fetchFieldsDictionary();
        dispatch({ type: 'SET_FIELDS_DICTIONARY', payload: fields });
      } catch (error) {
        console.error('Error loading fields dictionary:', error);
        // Don't show error toast for fields dictionary - it's not critical
      }
    };
    
    loadFieldsDictionary();
  }, []);

  // Use ref to track previous unifiedData to prevent unnecessary updates
  const prevUnifiedDataRef = useRef<UnifiedChatbotData | undefined>(undefined);
  const prevChatbotIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (unifiedData) {
      const dataChanged = prevUnifiedDataRef.current?.id !== unifiedData.id;
      const idChanged = prevChatbotIdRef.current !== unifiedData.id;
      
      // Only update from server data if there are no unsaved changes
      // This prevents overwriting user input with stale server data
      if (!state.isDirty && !state.unsavedChanges) {
        // Only update if data actually changed
        if (dataChanged || idChanged) {
          dispatch({ type: 'SET_CHATBOT', payload: unifiedData });
          dispatch({ type: 'RESET_DIRTY' });
          prevUnifiedDataRef.current = unifiedData;
          prevChatbotIdRef.current = unifiedData.id;
        }
      } else {
        // If there are unsaved changes, only update if the chatbot ID changed
        if (idChanged && state.chatbot?.id !== unifiedData.id) {
          // Chatbot ID changed, need to update
          dispatch({ type: 'SET_CHATBOT', payload: unifiedData });
          prevUnifiedDataRef.current = unifiedData;
          prevChatbotIdRef.current = unifiedData.id;
        }
        // Otherwise, preserve local changes and don't update from server
      }
    }
  }, [unifiedData?.id, state.isDirty, state.unsavedChanges]); // Only depend on ID, not entire object

  // Convenience methods
  const updateSettings = useCallback((updates: Partial<UnifiedChatbotSettings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: updates });
  }, []);

  const updateTheme = useCallback((updates: Partial<ChatTheme>) => {
    dispatch({ type: 'UPDATE_THEME', payload: updates });
  }, []);

  const updateQuestions = useCallback((questions: Question[]) => {
    dispatch({ type: 'UPDATE_QUESTIONS', payload: questions });
  }, []);

  const updateTriggerRules = useCallback((rules: TriggerRule[]) => {
    dispatch({ type: 'UPDATE_TRIGGER_RULES', payload: rules });
    // Don't auto-save - trigger rules are saved via the main Save Changes button
  }, []);

  const updateBasicInfo = useCallback((info: { name?: string; description?: string; title?: string; welcome_message?: string; completion_message?: string; operator_name?: string | null; operator_avatar_url?: string | null; company_name?: string | null; company_logo?: string | null }) => {
    dispatch({ type: 'UPDATE_BASIC_INFO', payload: info });
  }, []);

  const setDirty = useCallback((field: string, isDirty: boolean) => {
    dispatch({ type: 'SET_DIRTY', payload: { field, isDirty } });
  }, []);

  const resetDirty = useCallback(() => {
    dispatch({ type: 'RESET_DIRTY' });
  }, []);

  const setActiveTab = useCallback((tab: string) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  }, []);

  const setPendingOperatorAvatar = useCallback((file: File | null) => {
    dispatch({ type: 'SET_PENDING_OPERATOR_AVATAR', payload: file });
  }, []);

  const setRemoveOperatorAvatar = useCallback((remove: boolean) => {
    dispatch({ type: 'SET_REMOVE_OPERATOR_AVATAR', payload: remove });
  }, []);

  const setPendingCompanyLogo = useCallback((file: File | null) => {
    dispatch({ type: 'SET_PENDING_COMPANY_LOGO', payload: file });
  }, []);

  const setRemoveCompanyLogo = useCallback((remove: boolean) => {
    dispatch({ type: 'SET_REMOVE_COMPANY_LOGO', payload: remove });
  }, []);

  const clearPendingFiles = useCallback(() => {
    dispatch({ type: 'CLEAR_PENDING_FILES' });
  }, []);

  // Data access helpers
  const getChatbot = useCallback(() => state.chatbot, [state.chatbot]);
  
  const getSettings = useCallback((): UnifiedChatbotSettings => {
    return state.chatbot?.settings || DEFAULT_UNIFIED_SETTINGS;
  }, [state.chatbot]);

  const getTheme = useCallback(() => state.chatbot?.theme || null, [state.chatbot]);
  
  const getQuestions = useCallback(() => state.chatbot?.questions || [], [state.chatbot]);
  
  const getTriggerRules = useCallback(() => state.chatbot?.triggerRules || [], [state.chatbot]);

  const getFieldsDictionary = useCallback(() => state.fieldsDictionary || [], [state.fieldsDictionary]);

  // Context value
  const contextValue: BuilderContextType = {
    state,
    dispatch,
    updateSettings,
    updateTheme,
    updateQuestions,
    updateTriggerRules,
    updateBasicInfo,
    setDirty,
    resetDirty,
    setActiveTab,
    setPendingOperatorAvatar,
    setRemoveOperatorAvatar,
    setPendingCompanyLogo,
    setRemoveCompanyLogo,
    clearPendingFiles,
    getChatbot,
    getSettings,
    getTheme,
    getQuestions,
    getTriggerRules,
    getFieldsDictionary,
    debouncedSave,
    saveTheme,
    saveTriggerRules,
    refetch,
    mutations: {
      addQuestion: addQuestionMutation,
      updateQuestion: updateQuestionMutation,
      deleteQuestion: deleteQuestionMutation,
      updateQuestionsOrder: updateQuestionsOrderMutation,
      addTriggerRule: addTriggerRuleMutation,
      updateTriggerRule: updateTriggerRuleMutation,
      deleteTriggerRule: deleteTriggerRuleMutation
    }
  };

  return (
    <BuilderContext.Provider value={contextValue}>
      {children}
    </BuilderContext.Provider>
  );
};

// Hook to use the context
export const useBuilder = () => {
  const context = useContext(BuilderContext);
  if (context === undefined) {
    throw new Error('useBuilder must be used within a BuilderProvider');
  }
  return context;
};

// Hook to use builder with automatic save
export const useBuilderWithSave = (debounceMs: number = 1000) => {
  const builder = useBuilder();
  const { toast } = useToast();

  // TODO: Implement debounced save logic
  // This will be implemented in the next step with the unified data fetching hook

  return {
    ...builder,
    saveChanges: () => {
      // Placeholder for save functionality
      console.log('Save changes called');
    }
  };
};
